import { useEffect, useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import { useTranslation } from '@/hooks/useTranslation';

interface SaveFilterFormProps {
    open: boolean;
    defaultName?: string;
    defaultIsPublic?: boolean;
    isSaving?: boolean;
    onClose: () => void;
    onSave: (name: string, isPublic: boolean) => void;
}

const SaveFilterForm = ({
    open,
    defaultName = '',
    defaultIsPublic = false,
    isSaving = false,
    onClose,
    onSave,
}: SaveFilterFormProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState(defaultName);
    const [visibility, setVisibility] = useState(defaultIsPublic ? 'public' : 'private');

    useEffect(() => {
        if (open) {
            setName(defaultName);
            setVisibility(defaultIsPublic ? 'public' : 'private');
        }
    }, [open, defaultName, defaultIsPublic]);

    const handleSave = () => {
        onSave(name.trim(), visibility === 'public');
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
            <DialogContent
                variant="small"
                className="lg:tw-w-xl lg:tw-max-w-xl"
                onPointerDownOutside={(event) => event.preventDefault()}
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>
                        {t('ITEMS_SEARCH_FILTER.SAVE_CUSTOM_FILTER')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-flex tw-flex-col tw-gap-6">
                    <div className="tw-flex tw-flex-col tw-gap-2 md:tw-flex-row md:tw-gap-6 md:tw-items-center">
                        <label
                            htmlFor="filter-builder-name"
                            className="tw-text-t-sm tw-text-t-secondary tw-m-0 tw-min-w-0 md:tw-w-2/5 md:tw-shrink-0 md:tw-text-right"
                        >
                            {t('ITEMS_SEARCH_FILTER.FILTER_NAME')}
                        </label>
                        <div className="tw-w-full tw-min-w-0 md:tw-w-3/5">
                            <Input
                                id="filter-builder-name"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                className="tw-w-full"
                            />
                        </div>
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-2 md:tw-flex-row md:tw-gap-6 md:tw-items-start">
                        <span className="tw-text-t-sm tw-text-t-secondary tw-m-0 tw-min-w-0 md:tw-w-2/5 md:tw-shrink-0 md:tw-text-right">
                            {t('ITEMS_SEARCH_FILTER.WHO_CAN_SEE')}
                        </span>
                        <RadioGroup
                            value={visibility}
                            onValueChange={setVisibility}
                            className="tw-flex tw-flex-col tw-gap-2 tw-grid-cols-none tw-w-full tw-min-w-0 md:tw-w-3/5"
                        >
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <RadioGroupItem value="private" id="filter-visibility-private" />
                                <label
                                    htmlFor="filter-visibility-private"
                                    className="tw-text-t-base tw-text-t-primary tw-m-0"
                                >
                                    {t('ITEMS_SEARCH_FILTER.ONLY_YOU')}
                                </label>
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <RadioGroupItem value="public" id="filter-visibility-public" />
                                <label
                                    htmlFor="filter-visibility-public"
                                    className="tw-text-t-base tw-text-t-primary tw-m-0"
                                >
                                    {t('ITEMS_SEARCH_FILTER.EVERYONE_IN_ORG')}
                                </label>
                            </div>
                        </RadioGroup>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="default"
                        disabled={isSaving}
                        loading={isSaving}
                        onClick={handleSave}
                    >
                        {t('ITEMS_SEARCH_FILTER.SAVE_FILTER')}
                    </Button>
                    <DialogClose asChild={true}>
                        <Button variant="secondary" onClick={onClose}>
                            {t('CANCEL')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

SaveFilterForm.displayName = 'SaveFilterForm';

export { SaveFilterForm };
